// Audio synth engine with streaming output node

interface OscillatorSet {
  o: OscillatorNode;
  sub: OscillatorNode;
  g: GainNode;
  sg: GainNode;
}

interface ADSR {
  a: number;
  d: number;
  s: number;
  r: number;
}

export class SynthEngine {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  delay: DelayNode | null = null;
  fb: GainNode | null = null;
  rev: ConvolverNode | null = null;
  streamDest: MediaStreamAudioDestinationNode | null = null;
  private osc: Record<number, OscillatorSet> = {};

  init() {
    if (this.ctx) return;
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    this.ctx = new AC();

    this.master = this.ctx.createGain();
    this.master.gain.value = 0.18;

    this.delay = this.ctx.createDelay(1.0);
    this.fb = this.ctx.createGain();
    this.delay.delayTime.value = 0.3;
    this.fb.gain.value = 0.32;

    this.rev = this.ctx.createConvolver();
    this._reverb();

    this.delay.connect(this.fb);
    this.fb.connect(this.delay);
    this.master.connect(this.ctx.destination);
    this.delay.connect(this.master);
    this.rev.connect(this.master);

    // Create MediaStream audio capture sink for local screen recording
    this.streamDest = this.ctx.createMediaStreamDestination();
    this.master.connect(this.streamDest);
  }

  private _reverb() {
    if (!this.ctx) return;
    const rate = this.ctx.sampleRate || 44100;
    const len = rate * 2.4;
    const buf = this.ctx.createBuffer(2, len, rate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.0);
      }
    }
    if (this.rev) this.rev.buffer = buf;
  }

  play(midi: number, type: OscillatorType = 'triangle', adsr: ADSR = { a: 0.05, d: 0.2, s: 0.6, r: 1.2 }) {
    this.init();
    if (!this.ctx || !this.master || !this.delay || !this.rev) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();

    const f = 440 * Math.pow(2, (midi - 69) / 12);
    const now = this.ctx.currentTime;

    if (this.osc[midi]) this.stop(midi, adsr.r);

    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.value = f;

    const sub = this.ctx.createOscillator();
    const sg = this.ctx.createGain();
    sub.type = 'sawtooth';
    sub.frequency.value = f / 2;
    sg.gain.value = 0.16;

    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.5, now + adsr.a);
    g.gain.exponentialRampToValueAtTime(Math.max(adsr.s, 0.001), now + adsr.a + adsr.d);

    o.connect(g);
    sub.connect(sg);
    sg.connect(g);
    g.connect(this.master);
    g.connect(this.delay);
    g.connect(this.rev);

    o.start(now);
    sub.start(now);
    this.osc[midi] = { o, sub, g, sg };
  }

  stop(midi: number, r = 1.2) {
    const v = this.osc[midi];
    if (!v || !this.ctx) return;
    const now = this.ctx.currentTime;

    try {
      v.g.gain.cancelScheduledValues(now);
      v.g.gain.setValueAtTime(v.g.gain.value, now);
      v.g.gain.exponentialRampToValueAtTime(0.0001, now + r);
      setTimeout(() => {
        try {
          v.o.stop();
          v.sub.stop();
          v.o.disconnect();
          v.sub.disconnect();
          v.g.disconnect();
          v.sg.disconnect();
        } catch (e) { /* already stopped */ }
      }, r * 1000 + 80);
    } catch (e) { /* ignore */ }
    delete this.osc[midi];
  }
}